import React, { useState } from 'react';
import {
  Trophy,
  CheckCircle,
  XCircle,
  Plus,
  Edit,
  Trash2,
  Star,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import CreateVendorSuccessStoryModal from '../../components/admin/CreateVendorSuccessStoryModal';
import { useAdminVendorSuccessStories } from '../../hooks/useAdminVendorSuccessStories';
import { useToastContext } from '../../contexts/ToastContext';

const AdminVendorSuccessStories = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const { success, error: showError } = useToastContext();
  const {
    stories,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    statistics,
    createStory,
    isCreating,
    updateStory,
    isUpdating,
    toggleStatus,
    isToggling,
    deleteStory,
    isDeleting,
  } = useAdminVendorSuccessStories();

  const columns = [
    {
      key: 'vendor_name',
      label: 'Vendor',
      sortable: true,
      width: 'w-48',
      render: (story) => (
        <div className="flex items-center gap-3">
          {story.image && (
            <img
              src={story.image}
              alt={story.vendor_name}
              className="w-10 h-10 rounded-lg object-cover"
            />
          )}
          <div>
            <p className="text-sm font-medium text-gray-900">{story.vendor_name || 'N/A'}</p>
            {story.featured && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                <Star className="w-3 h-3 fill-current" />
                Featured
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: 'w-32',
      render: (story) => (
        <p className="text-sm text-gray-900 capitalize">{story.category || 'N/A'}</p>
      ),
    },
    {
      key: 'quote',
      label: 'Quote',
      sortable: false,
      width: 'w-64',
      render: (story) => (
        <p className="text-sm text-gray-600 line-clamp-2">"{story.quote}"</p>
      ),
    },
    {
      key: 'author',
      label: 'Author',
      sortable: true,
      width: 'w-40',
      render: (story) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{story.author}</p>
          {story.role && (
            <p className="text-xs text-gray-500">{story.role}</p>
          )}
        </div>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-28',
      render: (story) => (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            story.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {story.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (story) => {
        setEditingStory(story);
        setShowCreateModal(true);
      },
    },
    {
      label: (story) => (story.is_active ? 'Deactivate' : 'Activate'),
      icon: (story) => (story.is_active ? XCircle : CheckCircle),
      variant: 'default',
      onClick: (story) => {
        toggleStatus(story.id, {
          onSuccess: () => {
            success(
              'Status Updated',
              `Success story has been ${story.is_active ? 'deactivated' : 'activated'}.`
            );
          },
          onError: (err) => {
            showError('Status Update Failed', err?.response?.data?.message || 'Failed to update status.');
          },
        });
      },
      disabled: isToggling,
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (story) => {
        if (window.confirm('Are you sure you want to delete this success story?')) {
          deleteStory(story.id, {
            onSuccess: () => {
              success('Story Deleted', 'The success story has been deleted.');
            },
            onError: (err) => {
              showError('Deletion Failed', err?.response?.data?.message || 'Failed to delete.');
            },
          });
        }
      },
      disabled: isDeleting,
    },
  ];

  const handleCreate = (data) => {
    createStory(data, {
      onSuccess: () => {
        setShowCreateModal(false);
        setEditingStory(null);
        success('Story Created', 'The success story has been created.');
      },
      onError: (err) => {
        showError('Creation Failed', err?.response?.data?.message || 'Failed to create.');
      },
    });
  };

  const handleUpdate = (data) => {
    updateStory(
      { id: editingStory.id, data },
      {
        onSuccess: () => {
          setShowCreateModal(false);
          setEditingStory(null);
          success('Story Updated', 'The success story has been updated.');
        },
        onError: (err) => {
          showError('Update Failed', err?.response?.data?.message || 'Failed to update.');
        },
      }
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Success Stories</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load. Please try again.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Trophy className="w-6 h-6" />
                Vendor Success Stories
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage vendor success stories displayed on /vendor/success
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingStory(null);
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Story
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{statistics.total || 0}</p>
              </div>
              <Trophy className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{statistics.active || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Featured</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{statistics.featured || 0}</p>
              </div>
              <Star className="w-8 h-8 text-amber-500" />
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              options={activeStatusOptions}
            />
          </div>
        </div>

        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={stories}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search by vendor, author, or quote..."
            onSearch={(_, term) => handleFilterChange('search', term)}
            emptyMessage="No success stories found"
            pageSize={10}
            showPagination={true}
          />
        </div>

        {showCreateModal && (
          <CreateVendorSuccessStoryModal
            isOpen={showCreateModal}
            onClose={() => {
              setShowCreateModal(false);
              setEditingStory(null);
            }}
            onSave={editingStory ? handleUpdate : handleCreate}
            story={editingStory}
            isEdit={!!editingStory}
            isLoading={isCreating || isUpdating}
          />
        )}
      </div>
    </div>
  );
};

export default AdminVendorSuccessStories;
